<script setup lang="ts">
import { Workshop } from '@/@types/workshop'
import Event from '@/assets/main/event.webp'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useExpiredWorkshopDialog } from '@/composables/admin/useExpiredWorkshopDialog'
import { formatDateFull } from '@/helpers/format-date'
import { Calendar, Ticket } from 'lucide-vue-next'

const { workshop } = defineProps<{
    workshop: Workshop
}>()

const { selectedWorkshop, showExpiredConfirmation, handleWorkshopClick, closeExpiredConfirmation, onExpiredDialogClose, confirmExpiredNavigation } =
    useExpiredWorkshopDialog()
</script>

<template>
    <button type="button" class="w-full text-left" @click="handleWorkshopClick(workshop, 'mailings')">
        <Card class="flex h-auto flex-col gap-6 rounded-2xl border-none shadow-md">
            <CardHeader class="relative h-64 w-full rounded-2xl xl:h-80 2xl:h-90">
                <img :src="Event" alt="" class="absolute inset-0 h-full w-full rounded-2xl object-cover p-2" />
            </CardHeader>
            <CardContent class="flex flex-col gap-4">
                <span class="font-inter text-sm text-cyan-600 uppercase">
                    {{ $t('Forum') }}
                </span>
                <CardTitle class="font-sans text-xl text-gray-800">
                    {{ $t(workshop.description) }}
                </CardTitle>
                <CardDescription class="font-sans text-base text-gray-600">
                    {{
                        $t(
                            `Participate in the event that transforms Brazilian logistics and shapes the future of the supply chain. At the ILOS Forum, you will find the main...`,
                            { event_description: $t(workshop.description) },
                        )
                    }}
                </CardDescription>
                <hr />
            </CardContent>
            <CardFooter class="flex flex-col items-start gap-4 text-gray-600">
                <div class="flex gap-2">
                    <Calendar />
                    {{ formatDateFull(workshop.start, $i18n.locale) }}
                    -
                    {{ formatDateFull(workshop.end, $i18n.locale) }}
                </div>
                <div class="flex gap-2" v-if="workshop.mailings_count">
                    <Ticket />
                    {{ workshop.mailings_count }} {{ $t('Tickets purchased') }}
                </div>
            </CardFooter>
        </Card>
    </button>

    <AlertDialog :open="showExpiredConfirmation" @update:open="(isOpen) => !isOpen && onExpiredDialogClose()">
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ $t('Expired workshop management warning') }}</AlertDialogTitle>
                <AlertDialogDescription>
                    {{
                        $t(
                            'You are about to manage {workshop_description}, whose validity period has already expired. Do you really want to continue?',
                            { workshop_description: $t(selectedWorkshop?.description ?? '') },
                        )
                    }}
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel @click="closeExpiredConfirmation">{{ $t('No') }}</AlertDialogCancel>
                <AlertDialogAction @click="confirmExpiredNavigation('mailings')">{{ $t('Yes') }}</AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
